// src/pages/Profile/Profile.jsx

import React, { useEffect } from "react";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import {
  FaCamera,
  FaInfoCircle,
  FaPen,
  FaShieldAlt,
  FaSyncAlt,
} from "react-icons/fa";

import backgroundImage from "./fond_bkm29.jpg";
import useScrollToTop from "./../../hooks/useScollToTop";

import CropPhotoModal from "./../../components/CropPhotoModal/CropPhotoModal";

import useProfilePage from "./hooks/useProfilePage";

import ActionButton from "./ui/ActionButton";

import ProfileLoading from "./components/ProfileLoading";
import ProfileSidebar from "./components/ProfileSidebar";
import ProfileIdentityCards from "./components/ProfileIdentityCards";
import EmailNotificationsCard from "./components/EmailNotificationsCard";
import PushNotificationsCard from "./components/PushNotificationsCard";
import EditProfileModal from "./components/EditProfileModal";
import PhotoProfileModal from "./components/PhotoProfileModal";

export default function Profile() {
  useScrollToTop();

  const profile = useProfilePage();

  const {
    user,
    loading,
    userRole,
    roleMeta,
    displayName,
    previewSrc,
    imgVersion,
    imgOk,
    setImgOk,

    isEditing,
    setIsEditing,
    isPhotoModalOpen,
    setIsPhotoModalOpen,

    uploadMode,
    setUploadMode,
    isUploading,
    uploadProgress,
    outputFormat,
    setOutputFormat,
    fileInputRef,

    isCropOpen,
    setIsCropOpen,
    cropImageUrl,
    cropUserPhoto,

    isRefreshingRole,

    refreshUserProfile,
    handleCloseModal,
    handleChange,
    handleUserUpdated,
    handleSubmit,
    openPhotoModal,
    openFilePicker,
    handleFileChange,
    handlePhotoSave,
    handlePhotoUrlChange,
    handleToggleEmailNotifications,
    handleRefreshRole,
    handleCropped,
  } = profile;

  useEffect(() => {
    refreshUserProfile();
  }, [refreshUserProfile]);

  if (loading) {
    return <ProfileLoading backgroundImage={backgroundImage} />;
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url("${backgroundImage}")` }}
      />

      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/92 via-slate-950/75 to-slate-950/60" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />

      <div className="absolute left-[-120px] top-24 h-80 w-80 rounded-full bg-primary/25 blur-3xl" />
      <div className="absolute bottom-[-140px] right-[-100px] h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl items-center px-4 pb-12 pt-[calc(var(--site-navbar-h)+48px)] sm:px-6 lg:px-8">
        <section className="w-full overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/10 shadow-2xl backdrop-blur-2xl">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
            <ProfileSidebar
              previewSrc={previewSrc}
              displayName={displayName}
              email={user.email}
              roleMeta={roleMeta}
              imgVersion={imgVersion}
              imgOk={imgOk}
              setImgOk={setImgOk}
              openPhotoModal={openPhotoModal}
            />

            <section className="relative bg-white p-6 text-slate-950 dark:bg-slate-900 dark:text-white sm:p-8 lg:p-10">
              <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />

              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                  <FaShieldAlt />
                  Espace personnel
                </div>

                <h2 className="mt-5 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white">
                  Mon profil
                </h2>

                <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-500 dark:text-slate-400">
                  Gérez vos informations personnelles, votre photo de profil et
                  vos préférences de notification.
                </p>

                <ProfileIdentityCards
                  displayName={displayName}
                  email={user.email}
                />

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <ActionButton icon={FaPen} onClick={() => setIsEditing(true)}>
                    Modifier les informations
                  </ActionButton>

                  <ActionButton
                    icon={FaCamera}
                    onClick={openPhotoModal}
                    variant="secondary"
                  >
                    Modifier la photo
                  </ActionButton>

                  <ActionButton
                    icon={FaSyncAlt}
                    onClick={handleRefreshRole}
                    variant="secondary"
                    disabled={isRefreshingRole}
                  >
                    {isRefreshingRole
                      ? "Actualisation..."
                      : "Rafraîchir le rôle"}
                  </ActionButton>
                </div>

                {(userRole === "admin" || userRole === "enseignant") && (
                  <EmailNotificationsCard
                    enabled={!!user.email_notifications_enabled}
                    onToggle={handleToggleEmailNotifications}
                  />
                )}

                <PushNotificationsCard
                  user={user}
                  onUserUpdated={handleUserUpdated}
                />

                <div className="mt-8 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                  <div className="flex items-start gap-3">
                    <FaInfoCircle className="mt-1 shrink-0 text-primary" />
                    <p>
                      Après une modification importante du profil, une
                      déconnexion/reconnexion peut être nécessaire pour mettre à
                      jour l’affichage dans toute l’application.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </section>
      </div>

      {isEditing && (
        <EditProfileModal
          user={user}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onClose={handleCloseModal}
        />
      )}

      {isPhotoModalOpen && (
        <PhotoProfileModal
          user={user}
          uploadMode={uploadMode}
          setUploadMode={setUploadMode}
          fileInputRef={fileInputRef}
          openFilePicker={openFilePicker}
          handleFileChange={handleFileChange}
          handlePhotoSave={handlePhotoSave}
          handlePhotoUrlChange={handlePhotoUrlChange}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          outputFormat={outputFormat}
          setOutputFormat={setOutputFormat}
          imgVersion={imgVersion}
          imgOk={imgOk}
          setImgOk={setImgOk}
          setIsCropOpen={setIsCropOpen}
          onClose={() => setIsPhotoModalOpen(false)}
        />
      )}

      <CropPhotoModal
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        entityId={user?.id}
        imageUrl={cropImageUrl}
        onCropped={handleCropped}
        format="webp"
        cropAction={cropUserPhoto}
      />

      <ToastContainer />
    </main>
  );
}